import React, { useRef, useEffect, useState } from 'react';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Button } from '@/components/ui/button';
import { MoreVertical, Info } from 'lucide-react';
import { ChatMessage } from './ChatMessage';
import { ChatInput } from './ChatInput';
import type { ChatThread, ChatMessage as ChatMessageType } from '../types';

interface ChatMessageViewProps {
  thread: ChatThread;
  onSendMessage: (content: string) => void;
  onAttachment?: () => void;
  institutionName: string;
  matterReference?: string;
  deceasedName?: string;
  fullHeight?: boolean;
}

export function ChatMessageView({
  thread,
  onSendMessage,
  onAttachment,
  institutionName,
  matterReference: _matterReference,
  deceasedName: _deceasedName,
  fullHeight = false
}: ChatMessageViewProps) {
  const scrollAreaRef = useRef<HTMLDivElement>(null);
  const [isAtBottom, setIsAtBottom] = useState(true);

  // Group consecutive messages from same sender
  const groupedMessages = groupMessages(thread.messages);

  // Scroll to bottom on new messages if user is at bottom
  useEffect(() => {
    if (isAtBottom && scrollAreaRef.current) {
      const scrollContainer = scrollAreaRef.current.querySelector(
        '[data-radix-scroll-area-viewport]'
      );
      if (scrollContainer) {
        scrollContainer.scrollTop = scrollContainer.scrollHeight;
      }
    }
  }, [thread.messages, isAtBottom]);

  const handleScroll = (event: React.UIEvent<HTMLDivElement>) => {
    const element = event.currentTarget;
    const threshold = 100;
    const isNearBottom =
      element.scrollHeight - element.scrollTop - element.clientHeight <
      threshold;
    setIsAtBottom(isNearBottom);
  };

  if (fullHeight) {
    return (
      <>
        {/* Header - Simplified and modern */}
        <div className="border-border bg-card shrink-0 border-b px-4 py-3">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="bg-primary/10 flex h-10 w-10 items-center justify-center rounded-full">
                <span className="text-primary text-sm font-semibold">
                  {institutionName.charAt(0).toUpperCase()}
                </span>
              </div>
              <div className="flex-1">
                <h2 className="text-base font-semibold">{institutionName}</h2>
                {thread.unreadCount > 0 && (
                  <p className="text-muted-foreground text-xs">
                    {thread.unreadCount} new message
                    {thread.unreadCount === 1 ? '' : 's'}
                  </p>
                )}
              </div>
            </div>
            <div className="flex items-center gap-1">
              <Button variant="ghost" size="icon-sm">
                <Info className="h-4 w-4" />
                <span className="sr-only">Information</span>
              </Button>
              <Button variant="ghost" size="icon-sm">
                <MoreVertical className="h-4 w-4" />
                <span className="sr-only">More options</span>
              </Button>
            </div>
          </div>
        </div>

        {/* Messages container with padding for sticky input */}
        <div className="relative flex-1 overflow-hidden">
          <ScrollArea
            className="bg-card absolute inset-0 pb-22"
            ref={scrollAreaRef}
          >
            <div
              className="mx-auto max-w-4xl px-4 py-4"
              onScroll={handleScroll}
              data-radix-scroll-area-viewport
            >
              {groupedMessages.map((group, groupIndex) => (
                <div key={group[0].id}>
                  {/* Date separator */}
                  {shouldShowDateSeparator(
                    group[0].timestamp,
                    groupIndex > 0
                      ? groupedMessages[groupIndex - 1][0].timestamp
                      : null
                  ) && <DateSeparator date={group[0].timestamp} />}

                  {/* Messages in group */}
                  {group.map((message, messageIndex) => (
                    <ChatMessage
                      key={message.id}
                      message={message}
                      showTimestamp={messageIndex === group.length - 1}
                      isGrouped={messageIndex > 0}
                    />
                  ))}
                </div>
              ))}
            </div>
          </ScrollArea>

          {/* Sticky Input */}
          <div className="absolute right-0 bottom-0 left-0">
            <ChatInput
              onSend={onSendMessage}
              onAttach={onAttachment}
              placeholder={`Message ${institutionName}...`}
            />
          </div>
        </div>
      </>
    );
  }

  // Non-fullHeight layout
  return (
    <div className="flex h-full flex-col">
      {/* Header - Simplified and modern */}
      <div className="border-border bg-card border-b px-4 py-3">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="bg-primary/10 flex h-10 w-10 items-center justify-center rounded-full">
              <span className="text-primary text-sm font-semibold">
                {institutionName.charAt(0).toUpperCase()}
              </span>
            </div>
            <div className="flex-1">
              <h2 className="text-base font-semibold">{institutionName}</h2>
              {thread.unreadCount > 0 && (
                <p className="text-muted-foreground text-xs">
                  {thread.unreadCount} new message
                  {thread.unreadCount === 1 ? '' : 's'}
                </p>
              )}
            </div>
          </div>
          <div className="flex items-center gap-1">
            <Button variant="ghost" size="icon-sm">
              <Info className="h-4 w-4" />
              <span className="sr-only">Information</span>
            </Button>
            <Button variant="ghost" size="icon-sm">
              <MoreVertical className="h-4 w-4" />
              <span className="sr-only">More options</span>
            </Button>
          </div>
        </div>
      </div>

      {/* Messages */}
      <ScrollArea className="bg-card flex-1" ref={scrollAreaRef}>
        <div
          className="mx-auto max-w-4xl px-4 py-4"
          onScroll={handleScroll}
          data-radix-scroll-area-viewport
        >
          {groupedMessages.map((group, groupIndex) => (
            // eslint-disable-next-line @eslint-react/no-array-index-key -- groupedMessages derived from messages; group lacks stable id, list is append-only with no reordering
            <div key={groupIndex}>
              {/* Date separator */}
              {shouldShowDateSeparator(
                group[0].timestamp,
                groupIndex > 0
                  ? groupedMessages[groupIndex - 1][0].timestamp
                  : null
              ) && <DateSeparator date={group[0].timestamp} />}

              {/* Messages in group */}
              {group.map((message, messageIndex) => (
                <ChatMessage
                  key={message.id}
                  message={message}
                  showTimestamp={messageIndex === group.length - 1}
                  isGrouped={messageIndex > 0}
                />
              ))}
            </div>
          ))}
        </div>
      </ScrollArea>

      {/* Input */}
      <ChatInput
        onSend={onSendMessage}
        onAttach={onAttachment}
        placeholder={`Message ${institutionName}...`}
      />
    </div>
  );
}

/**
 * Group consecutive messages from the same sender
 */
function groupMessages(messages: ChatMessageType[]): ChatMessageType[][] {
  const groups: ChatMessageType[][] = [];
  let currentGroup: ChatMessageType[] = [];
  let lastSender: string | null = null;

  messages.forEach((message) => {
    if (message.sender !== lastSender && currentGroup.length > 0) {
      groups.push(currentGroup);
      currentGroup = [];
    }
    currentGroup.push(message);
    lastSender = message.sender;
  });

  if (currentGroup.length > 0) {
    groups.push(currentGroup);
  }

  return groups;
}

/**
 * Check if we should show a date separator between messages
 */
function shouldShowDateSeparator(
  current: Date,
  previous: Date | null
): boolean {
  if (!previous) return true;

  const currentDate = current.toDateString();
  const previousDate = previous.toDateString();

  return currentDate !== previousDate;
}

/**
 * Date separator component
 */
function DateSeparator({ date }: { date: Date }) {
  const formatDate = (date: Date): string => {
    const today = new Date();
    const yesterday = new Date(today);
    yesterday.setDate(yesterday.getDate() - 1);

    if (date.toDateString() === today.toDateString()) {
      return 'Today';
    } else if (date.toDateString() === yesterday.toDateString()) {
      return 'Yesterday';
    } else {
      return date.toLocaleDateString('en-US', {
        weekday: 'short',
        month: 'short',
        day: 'numeric',
        year: date.getFullYear() !== today.getFullYear() ? 'numeric' : undefined
      });
    }
  };

  return (
    <div className="my-6 flex items-center justify-center">
      <div className="bg-muted rounded-full px-3 py-1">
        <span className="text-foreground-secondary text-xs font-medium">
          {formatDate(date)}
        </span>
      </div>
    </div>
  );
}
